import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, Sparkles, MessageSquare, Calendar, PenTool, Zap, Settings, ArrowRight } from 'lucide-react';
import { FEATURES, FEATURES_HEADER, VITRINE_BASE } from '../constants';
import { useLanguage } from '../LanguageContext';

const ICONS = {
  crm:              <Users size={18} />,
  documents:        <FileText size={18} />,
  ia:               <Sparkles size={18} />,
  messagerie:       <MessageSquare size={18} />,
  agenda:           <Calendar size={18} />,
  signature:        <PenTool size={18} />,
  automatisations:  <Zap size={18} />,
  'business-logic': <Settings size={18} />,
};

const ENTRY = {
  crm:              { x: -90, y: -20 },
  documents:        { x:  90, y: -20 },
  ia:               { x: -70, y:  70 },
  messagerie:       { x:   0, y:  90 },
  agenda:           { x:  70, y:  70 },
  signature:        { x: -70, y:  70 },
  automatisations:  { x:   0, y:  90 },
  'business-logic': { x:  70, y:  70 },
};

export default function Features() {
  const { lang }    = useLanguage();
  const header      = FEATURES_HEADER[lang];
  const featList    = FEATURES[lang];

  const gridRef     = useRef(null);
  const cardRefs    = useRef({});
  const prevPos     = useRef(null);
  const orderedRef  = useRef(featList);
  const isSwapping  = useRef(false);
  const intervalRef = useRef(null);

  const [ordered,   setOrdered]   = useState(featList);
  const [assembled, setAssembled] = useState(false);

  // Réinitialise la liste quand la langue change
  useEffect(() => {
    setOrdered(FEATURES[lang]);
    orderedRef.current = FEATURES[lang];
  }, [lang]);

  useEffect(() => { orderedRef.current = ordered; }, [ordered]);

  /* ── FLIP ── */
  useLayoutEffect(() => {
    if (!prevPos.current) return;
    const snap = prevPos.current;
    prevPos.current = null;
    const timeouts = [];

    ordered.forEach(f => {
      const el = cardRefs.current[f.id];
      if (!el || !snap[f.id]) return;
      const prev = snap[f.id];
      const next = el.getBoundingClientRect();
      const dx   = prev.left - next.left;
      const dy   = prev.top  - next.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      el.style.transition = 'none';
      el.style.transform  = `translate(${dx}px, ${dy}px) scale(1.03)`;
      el.classList.add('pb-flying');
      el.getBoundingClientRect();
      el.style.transition = 'transform 0.72s cubic-bezier(0.34, 1.56, 0.64, 1)';
      el.style.transform  = '';
      timeouts.push(setTimeout(() => { el.style.transition = ''; el.classList.remove('pb-flying'); isSwapping.current = false; }, 740));
    });

    return () => timeouts.forEach(clearTimeout);
  }, [ordered]);

  /* ── Swap ── */
  const doSwap = useCallback(() => {
    if (isSwapping.current) return;
    const current = orderedRef.current;
    const groups  = {};
    current.forEach((f, i) => { (groups[f.span] = groups[f.span] || []).push(i); });
    const swappable = Object.values(groups).filter(g => g.length >= 2);
    if (!swappable.length) return;
    const group = swappable[Math.floor(Math.random() * swappable.length)];
    const a     = group[Math.floor(Math.random() * group.length)];
    let   b;
    do { b = group[Math.floor(Math.random() * group.length)]; } while (b === a);
    const positions = {};
    current.forEach(f => { const el = cardRefs.current[f.id]; if (el) positions[f.id] = el.getBoundingClientRect(); });
    prevPos.current    = positions;
    isSwapping.current = true;
    const next = [...current];
    [next[a], next[b]] = [next[b], next[a]];
    setOrdered(next);
  }, []);

  /* ── Animation d'entrée ── */
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('.pb-bento-card'));
    cards.forEach(c => {
      const id  = c.dataset.featureId;
      const dir = ENTRY[id] || { x: 0, y: 60 };
      c.style.cssText = `opacity:0; transform:translate(${dir.x}px,${dir.y}px) scale(0.88); transition:none;`;
    });
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      cards.forEach((c, i) => {
        setTimeout(() => {
          c.style.transition = 'opacity 0.65s ease, transform 0.65s cubic-bezier(0.22,1,0.36,1)';
          c.style.opacity    = '1';
          c.style.transform  = '';
          if (i === cards.length - 1) {
            setTimeout(() => { cards.forEach(c2 => c2.removeAttribute('style')); setAssembled(true); }, 700);
          }
        }, i * 115);
      });
      observer.disconnect();
    }, { threshold: 0.1 });
    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  /* ── Carousel de swaps ── */
  useEffect(() => {
    if (!assembled) return;
    intervalRef.current = setInterval(doSwap, 3600);
    return () => clearInterval(intervalRef.current);
  }, [assembled, doSwap]);

  return (
    <section className="pb-section pb-section-pad" id="fonctionnalites" data-testid="pb-features">
      <div className="pb-container">
        <div style={{ marginBottom: 56 }}>
          <div className="pb-label">{header.label}</div>
          <h2 className="pb-section-title">{header.title.split('\n').map((line, i) => (
            <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
          ))}</h2>
          <p className="pb-section-sub">{header.sub}</p>
        </div>

        <div
          ref={gridRef}
          className={`pb-bento-grid${assembled ? ' pb-bento-live' : ''}`}
          aria-label={header.ariaLabel}
          data-testid="bento-grid"
        >
          {ordered.map(f => (
            <div
              key={f.id}
              ref={el => { cardRefs.current[f.id] = el; }}
              className={`pb-bento-card ${f.span}`}
              data-feature-id={f.id}
              data-testid={`bento-card-${f.id}`}
            >
              <div className="pb-pins" aria-hidden="true"><span /><span /><span /></div>
              <div className="pb-bento-icon">{ICONS[f.id]}</div>
              <div className="pb-bento-feature-label">{f.label}</div>
              <div className="pb-bento-title">{f.title}</div>
              <p className="pb-bento-desc">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* ─── CTA Tester PitBoard ─── */}
        <div style={{ marginTop: 52, textAlign: 'center' }}>
          <Link
            to={`${VITRINE_BASE}/demo`}
            className="pb-btn-primary"
            data-testid="pb-test-pitboard-btn"
          >
            {header.testBtn}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
